import React, { FC, useEffect, useState } from 'react'
import { RouteComponentProps } from "@reach/router"
import _ from 'lodash'
import Isvg from 'react-inlinesvg'
// Components
import { Toolbar } from "components/subnav"
import ImportIcon from "components/import-export/import-icon"
import ExportIcon from "components/import-export/export-icon"
import Import from "components/import-export/import"
import DepartmentalLineItemsTable from "components/departmental-line-items/departmental-line-items-table"
import ChartDepartmentalLineItemsTable from "components/departmental-line-items/chart-departmental-line-items-table"
// Data
import api_match from "data/interface/api"
import importService from "data/import"
import inflationService from "data/inflation"
import lineItemsService from "data/line-items"
import systemLineItemsService from "data/system-line-items"
import areaMeasuresService from "data/area-measures"
import rentrollService from 'data/rent-roll'
import propertyService from 'data/property.ts'
import assetService from 'data/asset-list'
import { alphanum } from "components/table/lib/sort"
//Styles
import { DepartmentalLineItems_s } from "components/departmental-line-items/departmental-line-items-styles"
import { AddIcon } from "images/icons/tools"

const DepartmentalLineItems: FC<RouteComponentProps & {type: "COMMERCIAL" | "MULTIFAMILY" | "HOSPITALITY" | "DEPARTMENTAL"}> = (props) => {
  const [data, setData] = useState<object[]>([])
  const [lineItems, setLineItems] = useState<object[]>([])
  const [systemLineItems, setSystemLineItems] = useState<object[]>([])
  const [rentrollData, setRentrollData] = useState<object[]>([])
  const [rentrollInitSort, setRentrollInitSort] = useState<boolean>(false)
  const [inflation, setInflation] = useState<object[]>([])
  const [areaMeasures, setAreaMeasures] = useState<object[]>([])
  const [showImport, toggleShowImport] = useState<boolean>(false)
  const [property, setProperty] = useState<object|null>(null);
  const [propertyId, setPropertyId] = useState(propertyService.getStoredPropertyId())
  const [hasAssetWithChart, setHasAssetWithChart] = useState<boolean | null>(null)
  const [chartId, setChartId] = useState<number>(-1)
  const tableId = "line-items-table"

  useEffect(() => {
    importService.stream().subscribe(data => {
      toggleShowImport(data['showImport'])
    })

    toggleShowImport(false);

    const lineItemsSubscription = lineItemsService.stream().subscribe(data => {
      api_match(
        data,
        init  => setData(init.value),
        ok    => {
          setData(_.filter(ok.value, li => li.type === "hosp_dept_revenue" || li.type === "hosp_dept_expense"))
          setLineItems(ok.value)
        },
        err   => {
          setData(err.value) // backout any optimistic updates
        },
        fetch => {}
      )
    })

    const systemLineItemsSubscription = systemLineItemsService.stream().subscribe(data => {
      api_match(
        data,
        init  => setSystemLineItems(init.value),
        ok    => {
          setSystemLineItems(ok.value)
        },
        err   => {
          setSystemLineItems(err.value) // backout any optimistic updates
        },
        fetch => {}
      )
    })

    const propertySubscription = propertyService.stream().subscribe((data) => {
      if (data.value.length > 0){
        const property = _.find(data.value, {propertyid: data.current})
        setProperty(property)
        if(property.assetid) {
          assetService.fetchOne(property.assetid)
            .then((asset) => {
              if(_.get(asset, "chartid")) {
                setHasAssetWithChart(true)
                setChartId(_.get(asset, "chartid"))
              } else setHasAssetWithChart(false)
            })
            .catch(() => {})
        } else setHasAssetWithChart(false)
      }
      switch (data.state) {
        case 'ok':
          setPropertyId(data.current)
          break
      }
    })

    const inflationSubscription = inflationService.stream().subscribe(data => {
      api_match(
        data,
        init  => setInflation(init.value),
        ok    => {
          setInflation(ok.value)
        },
        err   => {
          setInflation(err.value) // backout any optimistic updates
        },
        fetch => {}
      )
    })

    const areaMeasuresSubscription = areaMeasuresService.stream().subscribe(data => {
      api_match(
        data,
        init  => setAreaMeasures(init.value),
        ok    => {
          setAreaMeasures(ok.value)
        },
        err   => {
          setAreaMeasures(err.value) // backout any optimistic updates
        },
        fetch => {}
      )
    })

    const rentrollSubscription = rentrollService.stream().subscribe(data => {
      api_match(
        data,
        init  => setRentrollData(init.value),
        ok    => {
          if(!rentrollInitSort && ok.value.length) { // sort only on first load
            setRentrollData([...ok.value].sort((a, b) => alphanum(_.get(a, "suite"), _.get(b, "suite"))))
            setRentrollInitSort(true)
          } else {
            setRentrollData(ok.value)
          }
        },
        err   => {
          setRentrollData(err.value) // backout any optimistic updates
        },
        fetch => {}
      )
    })

    return function cleanup() {
      lineItemsSubscription.unsubscribe()
      systemLineItemsSubscription.unsubscribe()
      propertySubscription.unsubscribe()
      inflationSubscription.unsubscribe()
      areaMeasuresSubscription.unsubscribe()
      rentrollSubscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (_.isNil(propertyId)) return
    propertyService.fetchProperty(propertyId).then((prop) => {
      if(_.get(prop, "type") !== "Hospitality") return

      lineItemsService.fetchLineItems(propertyId)
      systemLineItemsService.fetchSystemLineItems(propertyId)
      inflationService.fetchInflation(propertyId)
      areaMeasuresService.fetchAreaMeasures(propertyId)
      rentrollService.fetchRentRoll(propertyId, false)
    })
  }, [propertyId])

  const Add = () => {
    const defaults = property ? {...property['defaultslineitem']} : {}
    return (
      <a onClick={() => lineItemsService.create(propertyService.getStoredPropertyId(), defaults, props.type)}>
        <Isvg src={AddIcon} width="20px" />
      </a>
    )
  }

  return (
    <DepartmentalLineItems_s>
      <Toolbar>
        <Add />
        <ImportIcon />
        <ExportIcon template="lineitem" />
      </Toolbar>
      { showImport && <Import /> }
      { data && property && !_.isNil(hasAssetWithChart) && property['defaultslineitem'] && inflation && lineItems && areaMeasures && systemLineItems && rentrollData && !showImport &&
        (hasAssetWithChart ?
        <ChartDepartmentalLineItemsTable tableId={tableId} data={data} type={props.type}
          lineItemsData={lineItems}
          systemLineItemsData={systemLineItems}
          inflationData={inflation}
          areaMeasuresData={areaMeasures}
          rentrollData={rentrollData}
          chartId={chartId}
          defaults={property['defaultslineitem']} />
        :
        <DepartmentalLineItemsTable tableId={tableId} data={data} type={props.type}
          hasAsset={_.get(property, "assetid") ? true : false}
          lineItemsData={lineItems}
          systemLineItemsData={systemLineItems}
          inflationData={inflation}
          areaMeasuresData={areaMeasures}
          rentrollData={rentrollData}
          defaults={property['defaultslineitem']} />)
      }
    </DepartmentalLineItems_s>
  )
}

export default DepartmentalLineItems
